import React, { useEffect, useRef, useState } from 'react'

import { alpha, Box, Divider, IconButton, InputBase, Stack, Tooltip, Typography, useTheme } from '@mui/material'
/**
 * `Bookmark`, kept after trying `Layers`.
 *
 * The argument for `Layers` was that a view is one of several ARRANGEMENTS of the same
 * table, which stacked planes read as, whereas a bookmark points at the act of saving
 * rather than at what was saved. That reasoning is fine on paper and lost on sight — the
 * bookmark is simply more legible at 18px, and "saved thing you return to" turns out to
 * carry the meaning well enough. Recorded so the swap is not attempted a third time.
 *
 * `Eye` is the tempting literal choice for the word "view" and is the worst of the three:
 * it means visibility or preview everywhere else in this app.
 *
 * Either way the icon is a companion to the LABEL, never a replacement — this control
 * eventually surfaces a user-NAMED view, and no glyph can say "Unsold EU stock".
 *
 * ⚠️ THAT LAST LINE IS WHY THIS LABEL DROPS SECOND AND NOT FIRST (VRG-535), not why it
 * never drops. A narrow toolbar has no configuration in which all five labels fit, so
 * "never a replacement" is not on the menu — the choice is which label goes and how the
 * meaning is preserved when it does. Here it is preserved twice over: the `Tooltip` and
 * the `aria-label` both carry the ACTIVE VIEW'S NAME, so the glyph is never asked to say
 * "Unsold EU stock" on its own. Download goes before it because its glyph genuinely can
 * carry its meaning unaided; see the ladder in `toolbarCollapse.ts`.
 */
import { BookmarkIcon, PlusIcon, RotateCcwIcon, XIcon } from 'lucide-react'

import { Button, MenuButton } from '@repo/ui'
import { useSnackbar } from 'contexts'
import { useTableViews } from 'hooks'
// Direct file import rather than the `components` barrel: this file is reached FROM that
// barrel (components → Table → ViewsDropdown), so importing back through it is a cycle.
import { toolbarLabelSx } from 'components/input/ActionLine/toolbarCollapse'
import type { SavedTableView, TableViewState } from 'types'

interface ViewsDropdownProps {
  viewKey: string
  // The table's current filter/sort/column/density state (serializable).
  currentState: TableViewState
  // Apply a saved view's state back onto the table.
  applyState: (state: TableViewState | undefined) => void
  // Reset the whole table to defaults (filter + sort + column order/visibility/density).
  onReset?: () => void
  /**
   * READ-ONLY views supplied by the surface itself, listed above the user's own.
   *
   * Added for the Analytics page, where the old role tab row (Overview / Marketing /
   * Legal / Logistics) becomes four seeded starting points rather than four fixed
   * tabs. They are selectable and comparable exactly like a saved view — same
   * `TableViewState` envelope, same `stableStringify` comparison, same `applyState`
   * — but they carry no delete affordance because they are computed from source on
   * every load, not stored per user.
   *
   * OPTIONAL and defaulted to `[]`, so every existing table renders byte-identically.
   */
  seededViews?: SavedTableView[]
  /**
   * Which view the surface is showing when NOTHING has been explicitly selected.
   *
   * ## The bug this closes
   *
   * `activeViewId` is `null` until the user picks a view for the first time, and
   * `selected` was derived from it alone — so on a fresh account the Analytics page
   * rendered the Overview seed's cards while this control read the generic "Views" and
   * no row carried a check. The page was in a named view and said it was in none.
   * Verified before the fix: with `activeViewId.analytics === null`, the trigger read
   * "Views" for the whole load (12 samples over 4.8s — it never settles, there is no
   * race here), with the Overview cards on screen throughout.
   *
   * The same state is reachable after first use, which is what makes it more than a
   * first-run cosmetic: the footer's "Reset to default" calls `setActiveView(null)`,
   * so tidying up put the control back to claiming no view while showing the default
   * one. The fallback makes that row's label true — you land on the default view and
   * it says so.
   *
   * ## Why a fallback and NOT a write
   *
   * The tempting fix is to persist `activeViewId = defaultViewId` on first load, and it
   * is wrong twice over: it writes to user preferences as a side effect of merely
   * LOOKING at a page, and it makes "has never chosen a view" indistinguishable from
   * "chose Overview" — so improving the default seed later could no longer reach anyone
   * who had simply visited. Derived, this stays free: no request, no stored state, and
   * the distinction survives.
   *
   * It does NOT weaken the de-select behaviour, because it only supplies WHICH view to
   * compare against. `selected` still requires `currentState` to match that view's own
   * state, so the moment the user drags a card or applies a filter the label falls back
   * to "Views" exactly as it does for an explicitly-chosen view.
   *
   * OPTIONAL: a table that passes nothing behaves byte-identically to before.
   */
  defaultViewId?: string
}

const PERSIST_DEBOUNCE_MS = 600

// Order-insensitive serialization for comparing the live table state against a
// saved view's state. The saved snapshot round-trips through Postgres `jsonb`,
// which reorders object keys (by length) — so a raw JSON.stringify of the live
// state never byte-matches the saved one even when they're semantically identical,
// which would break both the "stay selected while it matches" highlight and the
// "de-select on drift" behavior. Recursively sorts object keys; arrays keep their
// order (sortModel precedence is meaningful).
const stableStringify = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`
  if (v && typeof v === 'object') {
    const obj = v as Record<string, unknown>
    return `{${Object.keys(obj)
      .sort()
      .map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(v) ?? 'null'
}

// Footer action rows (Save view / Reset) styled to match the Select's list items
// (rounded highlight, 44px, brand-tint hover) since they live in the menuBottomButton
// slot rather than being selectable Select items.
const FOOTER_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  width: '100%',
  // Match the @repo/ui Select list item: 48px tall, 24px (p:3) horizontal inset,
  // rounded highlight — so the footer rows are the same size as the saved-view rows.
  px: 3,
  minHeight: 48,
  borderRadius: 1.5,
  cursor: 'pointer',
  color: 'text.primary',
  // Neutral grey hover (these are actions like Sort's "Add Sort"/"Clear", not
  // selectable list items — those keep the brand-purple hover).
  '&:hover': { backgroundColor: 'action.hover' },
}

/**
 * Saved table views as a single toolbar dropdown (left of Filter), built on the
 * shared @repo/ui Select: saved views are the selectable items (active one carries
 * the check), each with a trailing delete; "Save view" + "Reset to default" live in
 * the menuBottomButton footer. Also owns restore-on-login (re-applying the last used
 * view state on mount when "save state on next login" is enabled).
 */
export const ViewsDropdown = ({
  viewKey,
  currentState,
  applyState,
  onReset,
  seededViews = [],
  defaultViewId,
}: ViewsDropdownProps) => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const {
    savedViews,
    activeViewId,
    saveStateOnLogin,
    lastViewState,
    isLoading,
    isSaving,
    saveView,
    deleteView,
    setActiveView,
    persistLastViewState,
  } = useTableViews(viewKey)

  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')

  // Restore last-used view state once, after preferences have loaded.
  const restoredRef = useRef(false)
  useEffect(() => {
    if (restoredRef.current || isLoading) return
    restoredRef.current = true
    if (saveStateOnLogin && lastViewState) {
      applyState(lastViewState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, saveStateOnLogin])

  // Persist last-used view state (debounced) once restore has run.
  useEffect(() => {
    if (!restoredRef.current || !saveStateOnLogin) return
    const handle = setTimeout(() => {
      persistLastViewState(currentState)
    }, PERSIST_DEBOUNCE_MS)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentState), saveStateOnLogin])

  // Seeded first, then the user's own. One list, so selection / comparison / the
  // active-view highlight have a single lookup and cannot disagree about which view
  // is showing.
  const allViews = [...seededViews, ...savedViews]

  const handleSelectView = (id: string) => {
    const view = allViews.find(v => v.id === id)
    if (!view) return
    setActiveView(id)
    applyState(view.state)
  }

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    try {
      await deleteView(id)
    } catch {
      notification({ type: 'error', title: 'Could not delete view', text: 'Please try again.' })
    }
  }

  const handleSaveNew = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      await saveView(trimmed, currentState)
      // Collapse the input; the new view appears in the list (menu stays open).
      setSaving(false)
      setName('')
    } catch {
      notification({ type: 'error', title: 'Could not save view', text: 'Please try again.' })
    }
  }

  // Clear any active saved view and reset filter/sort/columns to default.
  const handleReset = () => {
    setActiveView(null)
    onReset?.()
  }

  // Group subheaders only earn their place when BOTH kinds are present — a lone
  // "Default views" heading over a list with no alternative is a label for nothing.
  const grouped = seededViews.length > 0 && savedViews.length > 0

  // Views -> Select items. User-saved rows carry a trailing delete; seeded rows do
  // not, because there is nothing stored to delete.
  const items = [
    ...seededViews.map(view => ({
      label: view.name,
      value: view.id,
      ...(grouped ? { group: 'Default views' } : {}),
    })),
    ...savedViews.map(view => ({
      label: view.name,
      value: view.id,
      ...(grouped ? { group: 'Your views' } : {}),
      trailing: (
        <IconButton
          size="small"
          aria-label={`Delete view ${view.name}`}
          onClick={e => handleDelete(e, view.id)}
          sx={{
            opacity: 0.5,
            '&:hover': { opacity: 1, backgroundColor: alpha(theme.palette.error.main, 0.08) },
          }}
        >
          <XIcon size={14} strokeWidth={1.5} />
        </IconButton>
      ),
    })),
  ]

  // The active saved view stays "selected" only while the table still matches the
  // state it was saved with. The moment the user changes sort/filter/columns/density
  // (here or via the toolbar), currentState drifts from the saved snapshot and the
  // view de-selects. Derived (not a setActiveView(null) effect) on purpose: it's
  // instant, needs no write, and dodges the select-time race where currentState
  // flips to the new view before the persisted activeViewId catches up (which would
  // otherwise clobber the fresh selection back to null). Picking the view again
  // re-applies its state (handleSelectView -> applyState) so the match returns.
  //
  // `defaultViewId` fills in only when nothing has been chosen — see the prop's doc for
  // why this is a fallback rather than a persisted write. An explicit selection always
  // wins, so this can never override a user's own choice.
  const effectiveViewId = activeViewId ?? defaultViewId ?? null
  const activeView = allViews.find(v => v.id === effectiveViewId)
  const selected =
    activeView && stableStringify(currentState) === stableStringify(activeView.state)
      ? { label: activeView.name, value: activeView.id }
      : undefined

  // Bottom footer: Save view (expands an inline name input) + Reset to default.
  const footer = (
    <>
      <Divider sx={{ mb: 2 }} />
      {saving ? (
        // Inline expansion — stopPropagation keeps the menu's type-ahead from
        // hijacking keystrokes.
        <Box sx={{ px: 1.5, pb: 0.5 }} onKeyDown={e => e.stopPropagation()}>
          <InputBase
            autoFocus
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveNew()
              if (e.key === 'Escape') {
                setSaving(false)
                setName('')
              }
            }}
            placeholder="View name"
            sx={{
              px: 2,
              py: 0.75,
              mb: 1,
              fontSize: 14,
              borderRadius: 2,
              border: `1px solid ${theme.palette.primary[200]}`,
            }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="secondary" disableElevation onClick={() => { setSaving(false); setName('') }}>
              Cancel
            </Button>
            <Button variant="primary" disableElevation onClick={handleSaveNew} disabled={!name.trim() || isSaving}>
              Save
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={FOOTER_ROW_SX} onClick={() => setSaving(true)}>
          <PlusIcon size={16} strokeWidth={1.5} />
          <Typography variant="body2">Save view</Typography>
        </Box>
      )}
      {/*
        NOT SHOWN WHEN SEEDED VIEWS EXIST, because there it is a second way to do
        something the list above already does.

        "Reset to default" is `setActiveView(null)` followed by `onReset()`. On a surface
        with no seeded views that is the only route back to the shipped state, so it earns
        its row. On one WITH them it does not: Analytics' Overview seed is literally
        `seed('overview', 'Overview', DEFAULT_ANALYTICS_VIEW_STATE)` — the same object
        `onReset` applies — so the footer row and the first item in the list above it
        produce an identical result.

        ⚠️ The redundancy is recent and was completed by the `defaultViewId` fallback a few
        commits ago. Before that, `activeViewId = null` left the trigger reading "Views"
        with nothing checked, so the row at least produced a state you could not reach by
        picking Overview — a distinguishable outcome, even if an incoherent one (the
        default view on screen, and the control denying it). With the fallback, null
        renders AS Overview-selected, and the last difference between the two disappeared.

        Derived from `seededViews.length`, deliberately, rather than a `showReset` prop.
        A prop would let the two drift — a page could pass seeds and still show the row —
        and the condition is not a preference, it is a fact about whether another control
        already offers the same action. Analytics is the only surface passing `seededViews`
        today, so the other 32 `ViewsDropdown` consumers are untouched.
      */}
      {onReset && seededViews.length === 0 && (
        <Box sx={FOOTER_ROW_SX} onClick={handleReset}>
          <RotateCcwIcon size={15} strokeWidth={1.5} />
          <Typography variant="body2">Reset to default</Typography>
        </Box>
      )}
    </>
  )

  /**
   * The trigger reads the ACTIVE VIEW'S NAME, falling back to the generic "Views".
   *
   * Every other control in this toolbar reports its own state — the date field shows the
   * range, the type filter shows "Chart, Map +1" — and this one did not, so there was no
   * way to tell which view you were in without opening the menu. That is worse than it
   * sounds on the Analytics page, where a view changes WHICH CARDS EXIST rather than just
   * which rows are filtered: an unlabelled trigger leaves the page's entire contents
   * unattributed.
   *
   * It reads from `selected`, NOT from `activeView`, and the difference is deliberate.
   * `selected` is already the "and the table still matches the state it was saved with"
   * form — the moment the user changes a sort, filter or column, the view de-selects in
   * the list and the check disappears. Labelling the trigger from `activeView` instead
   * would leave it claiming a view whose state is no longer on screen, which is exactly
   * the misreport this change exists to remove. So the trigger and the check in the menu
   * are driven by one expression and cannot disagree.
   *
   * Seeded views need no special case: `allViews` is `[...seededViews, ...savedViews]`,
   * so a seeded view resolves through the same lookup and shows its own name ("Overview",
   * "Marketing"). Read-only-ness affects the DELETE affordance in the list, not the label.
   *
   * With no view applied the label is the literal string "Views", byte-identical to
   * before, so no existing table toolbar shifts by a pixel until someone applies a view.
   */
  const triggerLabel = selected?.label ?? 'Views'

  const trigger = (
    <Tooltip title={triggerLabel} enterDelay={600}>
    <Button
      size="large"
      variant="secondary"
      disableElevation
      /*
        The accessible name, and the ONE place in this row where it is not simply the
        visible text: it names the active view, so a screen-reader user hears "Unsold EU
        stock" rather than "Views" whichever width the button is at. The visible label is
        `display: none` in a narrow container, which takes it out of the accessibility tree
        as well as the layout, so a collapsed trigger without this announces as unnamed.

        No `title` attribute on the Button — the Tooltip above owns the hover affordance,
        and a native `title` on a Tooltip's own child is what produces a double tooltip.
      */
      aria-label={selected ? `Views: ${triggerLabel}` : 'Views'}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <BookmarkIcon size={18} strokeWidth={1.5} style={{ flexShrink: 0 }} />
        {/*
          Label collapses to icon-only as the TOOLBAR CONTAINER narrows — second of the five
          to go, after Download (VRG-535, `toolbarCollapse.ts`). It was a VIEWPORT query at
          `md`, which never fired at the width the row actually wraps at.

          No `toolbarStartIconSx` counterpart here: the icon and label are siblings of this
          `Stack`, whose `spacing` emits a `margin-left` on the LABEL, so hiding the label
          removes its gap with it. Only MUI's `startIcon` (Download, Filter, Sort) leaves a
          margin behind on the icon itself.
        */}
        <Typography
          variant="body2"
          /*
            A view name is free text, so it is capped and ellipsised rather than allowed
            to set the button's width: an unbounded label would push Filter / Sort /
            Layout off the toolbar row. 200px fits ~26 characters at body2, which covers
            every seeded name and any reasonable user-typed one; beyond that the full
            name is reachable on hover.

            ⚠️ THE NATIVE `title` THAT USED TO CARRY THE OVERFLOW IS GONE (VRG-535), and it
            had to be. It was chosen over a MUI `Tooltip` precisely because it was a plain
            attribute on a leaf element with no Tooltip wrapper — correct at the time, but
            the element it sat on is exactly the one that now disappears when the container
            narrows. A hover affordance that vanishes with the label is no use to the
            collapsed button, which is the state that needs it most. The `Tooltip` on the
            Button wraps something that is always there; it also reaches the icon-only
            button, which a `title` on the hidden span could not.

            `block`, not `inline`: `text-overflow` needs a block-level box. In a flex row
            an inline child is blockified anyway, so this is what was already computing —
            stated explicitly so the ellipsis cannot be silently lost by a later layout
            change.
          */
          sx={{
            // `block` — see the note just above; this is the display the label returns to
            // once the container is wide enough to show it at all.
            ...toolbarLabelSx('views', 'block'),
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {triggerLabel}
        </Typography>
      </Stack>
    </Button>
    </Tooltip>
  )

  return (
    <MenuButton
      button={trigger}
      items={items}
      selected={selected}
      onSelect={(item: { value: string }) => handleSelectView(item.value)}
      menuBottomButton={footer}
      // Show the in-menu search box only once the user has accumulated enough
      // saved views to need it (built into MenuButton; filters items by label).
      search={allViews.length >= 10}
      emptyText="No saved views yet"
      menuMinWidth={240}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
    />
  )
}
